// Unit display for the detailed weather widgets. Households choosing °F get
// U.S. customary units throughout (mph, inches, inHg, miles); °C gets metric.

import type { TemperatureUnit } from "../../../shared/models";
import { cToF, kphToMph } from "../../../shared/weather";

export function units(u: TemperatureUnit) {
  const us = u === "F";
  return {
    temp: (c: number) => (us ? cToF(c) : c),
    tempLabel: (c: number | null) => (c === null ? "—" : `${Math.round(us ? cToF(c) : c)}°`),
    speed: (kph: number) => (us ? kphToMph(kph) : kph),
    speedLabel: (kph: number | null) => (kph === null ? "—" : `${Math.round(us ? kphToMph(kph) : kph)} ${us ? "mph" : "km/h"}`),
    speedUnit: us ? "mph" : "km/h",
    /** Liquid precipitation. */
    rain: (mm: number) => (us ? mm / 25.4 : mm),
    rainLabel: (mm: number) => (us ? `${(mm / 25.4).toFixed(mm >= 25.4 ? 1 : 2)}″` : `${mm.toFixed(mm >= 10 ? 0 : 1)} mm`),
    snowLabel: (mm: number) => (us ? `${(mm / 25.4).toFixed(1)}″` : `${(mm / 10).toFixed(1)} cm`),
    pressureLabel: (hpa: number | null) => (hpa === null ? "—" : us ? `${(hpa * 0.02953).toFixed(2)} inHg` : `${hpa.toFixed(1)} hPa`),
    pressureDelta: (hpa: number) => (us ? `${(Math.abs(hpa) * 0.02953).toFixed(2)} inHg` : `${Math.abs(hpa).toFixed(1)} hPa`),
    distanceLabel: (m: number | null) => {
      if (m === null) return "—";
      if (us) {
        const mi = m / 1609.34;
        return mi >= 10 ? "10+ mi" : `${mi.toFixed(mi < 3 ? 1 : 0)} mi`;
      }
      const km = m / 1000;
      return km >= 16 ? "16+ km" : `${km.toFixed(km < 5 ? 1 : 0)} km`;
    },
    heightLabel: (m: number | null) => (m === null ? "—" : us ? `${(Math.round((m * 3.28084) / 100) * 100).toLocaleString("en-US")} ft` : `${(Math.round(m / 50) * 50).toLocaleString("en-US")} m`),
  };
}

/** How the air feels by dew point (°F thresholds, as forecasters use them). */
export function dewComfort(dewC: number | null): string | null {
  if (dewC === null) return null;
  const f = cToF(dewC);
  if (f < 35) return "Very dry";
  if (f < 50) return "Dry";
  if (f < 56) return "Comfortable";
  if (f < 61) return "Slightly humid";
  if (f < 66) return "Sticky";
  if (f < 71) return "Muggy";
  return "Oppressive";
}

export const CLOUD_NAMES: Record<string, string> = { FEW: "Few", SCT: "Scattered", BKN: "Broken", OVC: "Overcast", VV: "Obscured", CLR: "Clear", SKC: "Clear" };
